import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useRecoilState } from 'recoil';
import { MINUTE } from 'time-constants';
import { array, number, object, string } from 'yup';

import { apiConfig } from '@constants';
import { hasResetRefetchIntervalState, pollenIndexState } from '@store';

import { refetchIntervalAt3pAnd3a } from '../../helpers/refetchIntervalUtils';
import { toQueryString } from '../../helpers/StringUtils';
import { useGetUserLocation } from '../useGetUserLocation';

const pollenIndexArgumentsSchema = object({
  lat: number().required(),
  lng: number().required(),
});

const pollenIndexSchema = object({
  metadata: object({
    language: string(),
    transactionId: string(),
    version: string(),
    latitude: number(),
    longitude: number(),
    expireTimeGmt: number(),
    statusCode: number(),
  }),
  pollenForecast12hour: object({
    fcstValid: array(number()),
    fcstValidLocal: array(string()),
    dayInd: array(string()),
    num: array(number()),
    daypartName: array(string()),
    grassPollenIndex: array(number()),
    grassPollenCategory: array(string()),
    treePollenIndex: array(number()),
    treePollenCategory: array(string()),
    ragweedPollenIndex: array(number()),
    ragweedPollenCategory: array(string()),
  }),
});

const getApiUrl = ({ lat, lng, language }) =>
  [
    apiConfig.TWC_API_BASE_V2,
    apiConfig.TWC_API_POLLEN_INDEX,
    '/3day',
    toQueryString({
      language,
      apiKey: apiConfig.TWC_API_KEY,
      geocode: `${lat},${lng}`,
      format: 'json',
    }),
  ].join('');

const getPollenIndex = async ({ lat, lng }) => {
  const validateArguments = await pollenIndexArgumentsSchema.validate({
    lat,
    lng,
  });

  const url = getApiUrl(validateArguments);
  const { data } = await axios.get(url);

  return pollenIndexSchema.validate(data);
};

export const useGetPollenIndex = () => {
  const {
    coords: { lat, lng },
  } = useGetUserLocation();
  const [state, setState] = useRecoilState(pollenIndexState);
  const [refetchInterval, setRefetchInterval] = useState(10 * MINUTE);

  const [hasResetRefetchInterval, setHasResetRefetchInterval] = useRecoilState(hasResetRefetchIntervalState);

  const { pollenIndex: pollenIndexRefetched } = hasResetRefetchInterval;

  // Trigger refetch when Debug date is set
  useEffect(() => {
    if (Date.prototype.debugDate && !pollenIndexRefetched) {
      console.log('Refetching Pollen Index');
      refetchIntervalAt3pAnd3a({ setRefetchInterval });
      setHasResetRefetchInterval({
        ...hasResetRefetchInterval,
        pollenIndex: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Date.prototype.debugDate]);

  const { data, error, isFetched, isLoading } = useQuery(
    ['getPollenIndex', lat, lng],
    () => getPollenIndex({ lat, lng }),
    {
      enabled: Boolean(lat && lng),
      refetchInterval,
      refetchOnWindowFocus: false,
      retry: false,
      onSettled: _data => {
        if (_data) {
          setRefetchInterval(2 * MINUTE);
        } else {
          refetchIntervalAt3pAnd3a({ setRefetchInterval });
        }
      },
    },
  );

  useEffect(() => {
    setState({ data, error, isFetched, isLoading });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading, isFetched]);

  return state;
};
